import { useState, type FormEvent } from 'react';
import { probeToken, setToken } from './api';
import { PageShell } from './components/PageShell';

interface LoginProps {
  onAuthed: () => void;
}

type Status = 'idle' | 'checking' | 'empty' | 'invalid' | 'network';

export function Login({ onAuthed }: LoginProps) {
  const [value, setValue] = useState('');
  const [reveal, setReveal] = useState(false);
  const [status, setStatus] = useState<Status>('idle');

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const candidate = value.trim();
    if (!candidate) {
      setStatus('empty');
      return;
    }
    setStatus('checking');
    const result = await probeToken(candidate);
    if (result === 'ok') {
      setToken(candidate);
      onAuthed();
      return;
    }
    setStatus(result);
  }

  const errorMessage =
    status === 'empty'
      ? 'Paste the bearer token to sign in.'
      : status === 'invalid'
        ? 'That token was rejected. Double-check it against the password manager.'
        : status === 'network'
          ? "Couldn't reach the server. Check that the Mac mini is online."
          : null;

  const title = (
    <>
      <h1 className="text-2xl font-bold tracking-tight">
        <span className="text-brand-white">Capstone</span>{' '}
        <span className="text-brand-blue">Engineering</span>
      </h1>
      <span className="rounded-full bg-brand-blue/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-blue ring-1 ring-brand-blue/40">
        Sign in
      </span>
    </>
  );

  return (
    <PageShell title={title}>
      <section className="mx-auto flex w-full max-w-md flex-col gap-6 pb-6 pt-8">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold text-brand-charcoal">Access token required</h2>
          <p className="text-sm leading-relaxed text-brand-slate">
            Paste the Capstone timesheet bearer token from the team password manager. You'll only
            need to do this once on this device.
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal">
              Bearer token
            </span>
            <div className="flex items-stretch gap-2">
              <input
                type={reveal ? 'text' : 'password'}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  if (status !== 'idle' && status !== 'checking') setStatus('idle');
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                inputMode="text"
                aria-invalid={status === 'invalid'}
                aria-describedby={errorMessage ? 'login-error' : undefined}
                className="min-h-[44px] flex-1 rounded-lg bg-brand-white px-3 py-2 font-mono text-sm text-brand-charcoal outline-none ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
                placeholder="64-character token"
              />
              <button
                type="button"
                onClick={() => setReveal((v) => !v)}
                className="min-h-[44px] rounded-lg bg-brand-steel px-3 text-xs font-semibold uppercase tracking-wide text-brand-slate ring-1 ring-brand-steel hover:bg-brand-steel/70"
                aria-label={reveal ? 'Hide token' : 'Show token'}
              >
                {reveal ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          {errorMessage && (
            <p
              id="login-error"
              role="alert"
              className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200"
            >
              {errorMessage}
            </p>
          )}

          <button
            type="submit"
            disabled={status === 'checking'}
            className="mt-1 min-h-[44px] rounded-lg bg-brand-blue px-4 py-2 text-base font-semibold text-brand-white shadow-sm transition-colors hover:bg-brand-blue/90 disabled:cursor-not-allowed disabled:bg-brand-blue/60"
          >
            {status === 'checking' ? 'Checking…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-auto text-xs text-brand-slate">
          The token is stored locally on this device only. Use Sign out from the admin header to
          clear it.
        </p>
      </section>
    </PageShell>
  );
}
